/**
 * Lines files for the aircraft's nav system (MB's batch 4: R23 and F16; spec docs/specs/project-reporting/refly-list.md).
 *
 * A Zdas `.lns` file is a header `// <name> Zone=<zone> Hemi=<N|S>` and one `number x1 y1 x2 y2` row per line, in UTM
 * metres. Teslyn writes the rows exactly as the block files and the crews' own refly files lay them out (genuine
 * `refly_flt_16.lns`, H26007 Mt Morgan, reproduced byte for byte by the tests): the line number right-aligned in 10
 * characters, each coordinate right-aligned in 14 with two decimals, the block file's line ending (CRLF).
 *
 * Conventions kept from the project's own files:
 * - a patch or a reflight is the next version of the line: its last digit goes up by one (100070 → 100071), as Line
 *   review numbers it (`reflights.ts`);
 * - a patch runs in the planned direction of its line, from the end nearer the line's first point (x1, y1) to the
 *   other: the crew's file keeps the original first point and cuts back the far end;
 * - the header is the block's own lines file header, so the nav system reads the file as part of the block.
 *
 * Nothing here touches files: Teslyn Field reads and writes them through the core (`lines_file_context`,
 * `lines_file_save`, `lns_read`).
 */
import { parseLineNumber } from './lines.js';
import { utmToLatLng, utmZoneOfEpsg } from './map-geo.js';
import type { Block, PlannedLine } from './model.js';
import { pointAlong, type Segment } from './reflights.js';
import { formatZfid } from './zfid.js';

/** One row of a lines file. */
export interface LnsRow {
  readonly number: number;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/** The header's three parts. */
export interface LnsHeader {
  readonly name: string;
  readonly zone: number;
  readonly hemisphere: 'N' | 'S';
}

/** A lines file read leniently: the header and line ending as written, every row, and the rows that were not lines. */
export interface LnsDocument {
  /** The first non-blank row as written (`// F26048_Milgun_fnl Zone=50 Hemi=S`). */
  readonly header: string;
  /** Null when the header does not say the name, zone and hemisphere. */
  readonly parsed: LnsHeader | null;
  readonly eol: '\r\n' | '\n';
  /** Whether the last row ends with a line ending. */
  readonly final_eol: boolean;
  readonly rows: readonly LnsRow[];
  /** Rows that are not `number x1 y1 x2 y2` (1-based row in the file and its text). */
  readonly skipped: ReadonlyArray<{ readonly row: number; readonly text: string }>;
}

export class LnsError extends Error {
  override readonly name = 'LnsError';
}

const HEADER = /^\s*\/\/\s*(\S+)\s+Zone\s*=\s*(\d+)\s+Hemi\s*=\s*([NS])\b/i;
const ROW =
  /^\s*(\d+)\s+(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)(?:\s.*)?$/;

/** The header's name, zone and hemisphere, or null. */
export function parseLnsHeader(header: string): LnsHeader | null {
  const m = HEADER.exec(header);
  if (!m) return null;
  return { name: m[1]!, zone: Number.parseInt(m[2]!, 10), hemisphere: m[3]!.toUpperCase() as 'N' | 'S' };
}

/** `// <name> Zone=<zone> Hemi=<h>`, as Zdas and the Diurnal planner write it. */
export function lnsHeaderText(h: LnsHeader): string {
  return `// ${h.name} Zone=${h.zone} Hemi=${h.hemisphere}`;
}

/** Read a lines file. Throws an LnsError when it is empty. */
export function readLns(text: string): LnsDocument {
  const body = text.startsWith('﻿') ? text.slice(1) : text;
  const eol: '\r\n' | '\n' = body.includes('\r\n') ? '\r\n' : '\n';
  const raw = body.split('\n').map((r) => (r.endsWith('\r') ? r.slice(0, -1) : r));
  const first = raw.findIndex((r) => r.trim() !== '');
  if (first < 0) throw new LnsError('The lines file is empty.');
  const header = raw[first]!;
  const rows: LnsRow[] = [];
  const skipped: Array<{ row: number; text: string }> = [];
  for (let i = first + 1; i < raw.length; i++) {
    const t = raw[i]!;
    if (t.trim() === '') continue;
    const m = ROW.exec(t);
    if (!m) {
      skipped.push({ row: i + 1, text: t });
      continue;
    }
    rows.push({
      number: Number.parseInt(m[1]!, 10),
      x1: Number(m[2]),
      y1: Number(m[3]),
      x2: Number(m[4]),
      y2: Number(m[5]),
    });
  }
  return {
    header,
    parsed: parseLnsHeader(header),
    eol,
    final_eol: /\n$/.test(body),
    rows,
    skipped,
  };
}

/**
 * Column widths of a row, as every block file and crew refly file under `C:\TA_Survey` lays them out (8,973 rows
 * of 8,973 written by the planner): the line number in 10 characters, then the coordinates in 14, 14, 13 and 14.
 */
export const LNS_WIDTHS = [10, 14, 14, 13, 14] as const;

/** One row as the block files lay it out: `    200203     214427.82    7367106.45    214427.82    7382086.23`. */
export function lnsRowText(r: LnsRow): string {
  const [n, a, b, c, d] = LNS_WIDTHS;
  const f = (v: number, w: number): string => v.toFixed(2).padStart(w);
  return `${String(r.number).padStart(n)}${f(r.x1, a)}${f(r.y1, b)}${f(r.x2, c)}${f(r.y2, d)}`;
}

/** The text of a lines file: the header, then the rows, with the line ending given (CRLF unless told). */
export function lnsText(input: {
  readonly header: string;
  readonly rows: readonly LnsRow[];
  readonly eol?: '\r\n' | '\n';
  /** End the last row with a line ending (default true; the crew's genuine refly file has none). */
  readonly finalEol?: boolean;
}): string {
  if (input.rows.length === 0) throw new LnsError('There are no lines to write.');
  const eol = input.eol ?? '\r\n';
  const text = [input.header, ...input.rows.map(lnsRowText)].join(eol);
  return input.finalEol === false ? text : text + eol;
}

// ------------------------------------------------------------------------------------------------- the .xyz and .KML beside it

/*
 * Decision 831 (MB, 27 Oct 2026): every refly or patch lines file Teslyn writes into a block's Reflys folder gets a
 * `.xyz` and a `.KML` of the same name beside it, laid out as MB's own files made from Teslyn's
 * `F26048_Milgun_Refly_20261027.lns` (golden files in `tests/fixtures/f26048/reflys/`). The crew's genuine
 * `refly_flt_16.xyz` (H26007, Day 177) has the same layout, so the `.xyz` is the one the crews already use.
 * Decision 849 (27 Oct): "Merge files" and "Split a file" write them too. Decision 848 (27 Oct): the `.KML` lines are
 * `relativeToGround`, at the survey height of the block they belong to, never a fixed height.
 */

/** The first row of the `.xyz`: a `/` comment with the column names right-aligned over their columns. */
export const LNS_XYZ_HEADER = `/${'X'.padStart(11)}${'Y'.padStart(13)}`;

/** Each point's columns in the `.xyz`: X right-aligned in 12 characters, Y in 13, two decimals. */
export const LNS_XYZ_WIDTHS = [12, 13] as const;

/**
 * The words before the line number on a line's row of the `.xyz`: `Line 9102731` for line 102731. The 9 is written
 * as MB's file and the crew's `refly_flt_16.xyz` (`Line 9200203`) write it; no document says what it stands for.
 */
export const LNS_XYZ_LINE_PREFIX = 'Line 9';

/** The `.xyz` text of some lines: the header row, then per line its `Line 9<number>` row and its two ends. */
export function lnsXyzText(input: {
  readonly rows: readonly LnsRow[];
  readonly eol?: '\r\n' | '\n';
}): string {
  if (input.rows.length === 0) throw new LnsError('There are no lines to write.');
  const eol = input.eol ?? '\r\n';
  const [wx, wy] = LNS_XYZ_WIDTHS;
  const point = (x: number, y: number): string => `${x.toFixed(2).padStart(wx)}${y.toFixed(2).padStart(wy)}`;
  const out = [LNS_XYZ_HEADER];
  for (const r of input.rows)
    out.push(`${LNS_XYZ_LINE_PREFIX}${r.number}`, point(r.x1, r.y1), point(r.x2, r.y2));
  return out.join(eol) + eol;
}

/** The KML style every line of the `.KML` uses, as MB's file: green at half opacity, 4 wide. */
export const LNS_KML_STYLE_ID = 'SurveyLineStyle';

/**
 * The altitude mode of every line (decision 848, MB's answer B to Q755): `relativeToGround`, the KML 2.2 mode that
 * means "this many metres above the ground", so Google Earth draws each line at its block's survey height over the
 * terrain, as the aircraft flies it. MB's own file wrote `relative`, which is not a KML 2.2 mode (KML 2.2 has
 * `clampToGround`, `relativeToGround` and `absolute`): a reader that does not know it falls back to `clampToGround`
 * and lays the lines on the ground whatever height they carry. His file stays the layout reference.
 */
export const LNS_KML_ALTITUDE_MODE = 'relativeToGround';

const xmlText = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** `Trav 102731` or `Tie 190351`: ties by the second digit 9, as "Split a file" and the planning software name them. */
export function lnsKmlLineName(number: number): string {
  return `${parseLineNumber(number).kind === 'tie' ? 'Tie' : 'Trav'} ${number}`;
}

/**
 * The `.KML` text of some lines for Google Earth: the document named after the file, the lines file's name and UTM
 * zone as its description, one placemark per line from its first point to its second in WGS 84 degrees (8 decimals)
 * at `altitudeM` above the ground (`relativeToGround`). The zone and hemisphere are the lines file header's.
 */
export function lnsKmlText(input: {
  /** The file's base name (no extension), `F26048_Milgun_Refly_20261027`. */
  readonly name: string;
  /** The lines file header's name and zone (`F26048_Milgun_fnl`, 50, S). */
  readonly header: LnsHeader;
  readonly rows: readonly LnsRow[];
  /**
   * Metres above the ground, written with two decimals: one height for every line, or each line's own (a merged or
   * split file's lines take the survey height of the block each one belongs to, decision 849).
   */
  readonly altitudeM: number | ((row: LnsRow) => number);
  readonly eol?: '\r\n' | '\n';
}): string {
  if (input.rows.length === 0) throw new LnsError('There are no lines to write.');
  const eol = input.eol ?? '\r\n';
  const height = input.altitudeM;
  const altOf = (r: LnsRow): string => (typeof height === 'number' ? height : height(r)).toFixed(2);
  const { zone, hemisphere } = input.header;
  const at = (x: number, y: number, alt: string): string => {
    const p = utmToLatLng(x, y, zone, hemisphere);
    return `          ${p.lng.toFixed(8)},${p.lat.toFixed(8)},${alt}`;
  };
  const out = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<kml xmlns="http://www.opengis.net/kml/2.2">',
    '  <Document>',
    `    <name>${xmlText(input.name)}</name>`,
    `    <description>${xmlText(`${input.header.name} Zone=${zone}`)}</description>`,
    `    <Style id="${LNS_KML_STYLE_ID}">`,
    '      <LineStyle>',
    '        <color>7f00ff00</color>',
    '        <width>4</width>',
    '      </LineStyle>',
    '      <PolyStyle>',
    '        <color>7f00ff00</color>',
    '      </PolyStyle>',
    '    </Style>',
  ];
  for (const r of input.rows) {
    const alt = altOf(r);
    out.push(
      '    <Placemark>',
      `      <name>${lnsKmlLineName(r.number)}</name>`,
      `      <styleUrl>#${LNS_KML_STYLE_ID}</styleUrl>`,
      '      <LineString>',
      '        <extrude>1</extrude>',
      `        <altitudeMode>${LNS_KML_ALTITUDE_MODE}</altitudeMode>`,
      '        <coordinates>',
      at(r.x1, r.y1, alt),
      at(r.x2, r.y2, alt),
      '        </coordinates>',
      '      </LineString>',
      '    </Placemark>',
    );
  }
  out.push('  </Document>', '</kml>');
  return out.join(eol) + eol;
}

/** The names of the `.xyz` and `.KML` beside a lines file: its base name with those extensions. */
export function lnsCompanionNames(lnsName: string): {
  readonly base: string;
  readonly xyz: string;
  readonly kml: string;
} {
  const base = lnsName.trim().replace(/\.lns$/i, '');
  return { base, xyz: `${base}.xyz`, kml: `${base}.KML` };
}

/** The height the `.KML` lines are drawn at: the block's survey height when set above 0, else 0 m (on the ground). */
export function kmlSurveyHeight(surveyHeightM: number | null | undefined): number {
  return typeof surveyHeightM === 'number' && Number.isFinite(surveyHeightM) && surveyHeightM > 0
    ? surveyHeightM
    : 0;
}

/** The three names a refly or patch lines file is saved under, for the preview: `X.lns, X.xyz and X.KML`. */
export function lnsFileSetWords(lnsName: string): string {
  const n = lnsCompanionNames(lnsName);
  return `${n.base}.lns, ${n.xyz} and ${n.kml}`;
}

/** What the `.KML`'s height is, for the preview. */
export function kmlHeightWords(surveyHeightM: number | null | undefined): string {
  const h = kmlSurveyHeight(surveyHeightM);
  return h > 0
    ? `The KML draws the lines ${h} m above the ground, the block's survey height.`
    : 'The block has no survey height, so the KML draws the lines on the ground (set it in Project settings, QC limits).';
}

/** A saved set for the result message: the lines file's full path, then the names of the files beside it. */
export function savedFileSetWords(path: string, companionPaths: readonly string[] | undefined): string {
  const names = (companionPaths ?? []).map((p) => p.split(/[\\/]/).pop() ?? p);
  return names.length === 0 ? path : `${path} with ${names.join(' and ')}`;
}

/** A file written beside a lines file, with the same base name. */
export interface LnsCompanion {
  readonly file_name: string;
  readonly text: string;
}

/** A block as the lines file tools see it, for the height of a line in the `.KML`. */
export type LnsHeightBlock = Pick<
  Block,
  'id' | 'name' | 'digit' | 'kind' | 'terrain_clearance_m' | 'deleted_at'
>;

/** The lines of a file that belong to one block, and the height the `.KML` draws them at. */
export interface LnsHeightGroup {
  /** The block's name; null when the file's block is not one of the project's (a block taken away). */
  readonly blockName: string | null;
  /** The block's survey height, 0 (on the ground) when it has none (`kmlSurveyHeight`). */
  readonly heightM: number;
  readonly lines: number;
  /**
   * The lines are of no block of this project (test lines below 10000, a line of another job): drawn at the height
   * of the block the file is saved in.
   */
  readonly assumed: boolean;
}

/**
 * The survey height of the block each row of a merged or split lines file belongs to (decision 849, MB 27 Oct 2026:
 * "Merge files" and "Split a file" write the `.xyz` and `.KML` too, "the height of the file's block"). A row's block is
 * the block of its planned line (the row's number, else its original line's: a patch `100071` is line `100070`'s),
 * else the survey block whose digit is the row's first digit; a row of neither is drawn at the height of the block
 * the file is saved in, and the preview says so. A file of lines from several blocks draws each line at its own
 * block's height: a KML line carries its own height, so no line is drawn at a height that is not its block's.
 */
export function lnsBlockHeights(input: {
  readonly rows: readonly LnsRow[];
  readonly blocks: readonly LnsHeightBlock[];
  readonly plan: ReadonlyArray<Pick<PlannedLine, 'number' | 'block_id' | 'deleted_at'>>;
  /** The block the file is saved in. */
  readonly savedInBlockId: string;
}): { readonly heightOf: (row: LnsRow) => number; readonly groups: readonly LnsHeightGroup[] } {
  const live = input.blocks.filter((b) => b.deleted_at === null);
  const byId = new Map(live.map((b) => [b.id, b]));
  const byDigit = new Map<string, LnsHeightBlock>();
  for (const b of live) if (b.kind === 'survey' && !byDigit.has(b.digit)) byDigit.set(b.digit, b);
  const lineBlock = new Map<number, string>();
  for (const p of input.plan) if (p.deleted_at === null) lineBlock.set(p.number, p.block_id);
  const savedIn = byId.get(input.savedInBlockId) ?? null;
  const blockOf = (r: LnsRow): { block: LnsHeightBlock | null; assumed: boolean } => {
    const id = lineBlock.get(r.number) ?? lineBlock.get(r.number - (r.number % 10));
    const planned = id ? byId.get(id) : undefined;
    if (planned) return { block: planned, assumed: false };
    const digit = r.number >= 10000 ? parseLineNumber(r.number).blockDigit : null;
    const byNumber = digit ? byDigit.get(digit) : undefined;
    if (byNumber) return { block: byNumber, assumed: false };
    return { block: savedIn, assumed: true };
  };
  const groups = new Map<
    string,
    { blockName: string | null; heightM: number; lines: number; assumed: boolean }
  >();
  const heights = new Map<LnsRow, number>();
  for (const r of input.rows) {
    const { block, assumed } = blockOf(r);
    const heightM = kmlSurveyHeight(block?.terrain_clearance_m);
    heights.set(r, heightM);
    const key = `${assumed ? 'assumed:' : ''}${block?.id ?? ''}`;
    const g = groups.get(key) ?? { blockName: block?.name ?? null, heightM, lines: 0, assumed };
    g.lines += 1;
    groups.set(key, g);
  }
  return {
    heightOf: (r) => heights.get(r) ?? kmlSurveyHeight(blockOf(r).block?.terrain_clearance_m),
    groups: [...groups.values()],
  };
}

/** The `.KML`'s heights of a merged or split file, for the preview (`lnsBlockHeights`). */
export function kmlBlockHeightsWords(groups: readonly LnsHeightGroup[]): string {
  const at = (h: number): string => (h > 0 ? `${h} m above the ground` : 'on the ground (no survey height)');
  const lines = (n: number): string => `${n} line${n === 1 ? '' : 's'}`;
  const known = groups.filter((g) => !g.assumed);
  const assumed = groups.filter((g) => g.assumed);
  const parts: string[] = [];
  if (known.length === 1 && assumed.length === 0) {
    const g = known[0]!;
    return g.heightM > 0
      ? `The KML draws the lines ${g.heightM} m above the ground, the survey height of ${g.blockName ?? 'their block'}.`
      : `${g.blockName ?? 'Their block'} has no survey height, so the KML draws the lines on the ground (set it in Project settings, QC limits).`;
  }
  if (known.length > 0)
    parts.push(
      `The KML draws each line at the survey height of its block: ${known
        .map(
          (g) =>
            `${g.blockName ?? 'a block'} ${g.heightM > 0 ? `${g.heightM} m` : 'on the ground (no survey height)'} (${lines(g.lines)})`,
        )
        .join(', ')}.`,
    );
  for (const g of assumed)
    parts.push(
      `${lines(g.lines)} of no block of this project ${g.lines === 1 ? 'is' : 'are'} drawn ${at(g.heightM)}, the survey height of ${g.blockName ?? 'the block the file is saved in'}, where the file is saved.`,
    );
  return parts.join(' ');
}

/**
 * The `.xyz` and `.KML` to write beside a lines file Teslyn writes into a Reflys folder (decisions 831 and 849). The
 * KML's UTM zone is the header's, else the block's EPSG code's; with neither, only the `.xyz` is written (its metres
 * need no zone). Its height is the block's survey height (Project settings, QC limits; `kmlSurveyHeight`), or each
 * line's own block's (`heightOf`, from `lnsBlockHeights`, for merged and split files).
 */
export function lnsCompanions(input: {
  readonly lnsName: string;
  readonly header: string;
  readonly rows: readonly LnsRow[];
  readonly eol?: '\r\n' | '\n';
  readonly surveyHeightM: number | null | undefined;
  /** Each line's height, when the lines are of several blocks (`lnsBlockHeights`); else `surveyHeightM` for all. */
  readonly heightOf?: (row: LnsRow) => number;
  readonly epsg?: number | null;
}): LnsCompanion[] {
  const names = lnsCompanionNames(input.lnsName);
  const eol = input.eol ?? '\r\n';
  const out: LnsCompanion[] = [{ file_name: names.xyz, text: lnsXyzText({ rows: input.rows, eol }) }];
  const fromEpsg = utmZoneOfEpsg(input.epsg);
  const header = parseLnsHeader(input.header) ?? (fromEpsg ? { name: names.base, ...fromEpsg } : null);
  if (header)
    out.push({
      file_name: names.kml,
      text: lnsKmlText({
        name: names.base,
        header,
        rows: input.rows,
        altitudeM: input.heightOf ?? kmlSurveyHeight(input.surveyHeightM),
        eol,
      }),
    });
  return out;
}

// ------------------------------------------------------------------------------------------------- patch lines

/** Metres from (x1, y1) along `line` of the foot of a point, not limited to the line. */
function alongOf(line: Pick<PlannedLine, 'x1' | 'y1' | 'x2' | 'y2'>, x: number, y: number): number {
  const len = Math.hypot(line.x2 - line.x1, line.y2 - line.y1);
  if (len === 0) return 0;
  return ((x - line.x1) * (line.x2 - line.x1) + (y - line.y1) * (line.y2 - line.y1)) / len;
}

/** Perpendicular distance in metres from a point to the line through `line`. */
function offsetOf(line: Pick<PlannedLine, 'x1' | 'y1' | 'x2' | 'y2'>, x: number, y: number): number {
  const len = Math.hypot(line.x2 - line.x1, line.y2 - line.y1);
  if (len === 0) return Math.hypot(x - line.x1, y - line.y1);
  return Math.abs((x - line.x1) * (line.y2 - line.y1) - (y - line.y1) * (line.x2 - line.x1)) / len;
}

/** A point further than this from the planned line is refused as a patch end. */
export const PATCH_END_MAX_OFFSET_M = 500;

/**
 * Where a flown run's fiducials lie on its line: the run's first and last ZFID at its logged start and end (metres
 * along the line). A ZFID between them is placed by distance, at the flight's average speed along the run.
 */
export interface FidRun {
  readonly fid_start: number;
  readonly fid_end: number;
  readonly start_m: number;
  readonly end_m: number;
}

/** Metres along the line of a ZFID of `run` (null when the ZFID is outside the run or the run has no length). */
export function alongOfFid(run: FidRun, fid: number): number | null {
  const span = run.fid_end - run.fid_start;
  if (!(span > 0) || fid < run.fid_start || fid > run.fid_end) return null;
  return Math.round((run.start_m + ((fid - run.fid_start) / span) * (run.end_m - run.start_m)) * 100) / 100;
}

/** The ZFID at metres `along` the line, or null when that point was not flown in the run. */
export function fidAtAlong(run: FidRun, along: number): number | null {
  const lo = Math.min(run.start_m, run.end_m);
  const hi = Math.max(run.start_m, run.end_m);
  const len = run.end_m - run.start_m;
  if (len === 0 || along < lo - 0.005 || along > hi + 0.005) return null;
  const t = (along - run.start_m) / len;
  return Math.round(run.fid_start + t * (run.fid_end - run.fid_start));
}

/** One end of a patch, as the tools take it. */
export type PatchEndInput =
  | { readonly by: 'along'; readonly m: number }
  | { readonly by: 'point'; readonly x: number; readonly y: number }
  | { readonly by: 'zfid'; readonly fid: number };

/**
 * The segment of `original` between two ends (F16 "make patch lines"): metres along the line, a coordinate (placed on
 * the line, refused when more than 500 m off it), or a ZFID of the flown run. The ends may be given in either order;
 * the segment always runs in the line's planned direction. Throws an LnsError with words for the screen.
 */
export function patchSegment(
  original: Pick<PlannedLine, 'number' | 'x1' | 'y1' | 'x2' | 'y2' | 'length_m'>,
  from: PatchEndInput,
  to: PatchEndInput,
  run?: FidRun | null,
): Segment {
  const place = (e: PatchEndInput, which: string): number => {
    if (e.by === 'along') {
      if (!Number.isFinite(e.m))
        throw new LnsError(`Enter the ${which} of the patch in metres along the line.`);
      return e.m;
    }
    if (e.by === 'point') {
      if (!Number.isFinite(e.x) || !Number.isFinite(e.y))
        throw new LnsError(`Enter the ${which} of the patch as an easting and a northing.`);
      const off = offsetOf(original, e.x, e.y);
      if (off > PATCH_END_MAX_OFFSET_M)
        throw new LnsError(
          `The ${which} of the patch is ${Math.round(off).toLocaleString('en-AU')} m off line ${original.number}; ` +
            `a patch end must be within ${PATCH_END_MAX_OFFSET_M} m of the line.`,
        );
      return alongOf(original, e.x, e.y);
    }
    if (!run) throw new LnsError(`Line ${original.number} has no flight data QC run to place a ZFID on.`);
    const at = alongOfFid(run, e.fid);
    if (at === null)
      throw new LnsError(
        `ZFID ${formatZfid(e.fid)} is not in the flown run of line ${original.number} (${formatZfid(run.fid_start)} to ${formatZfid(run.fid_end)}).`,
      );
    return at;
  };
  const a = place(from, 'start');
  const b = place(to, 'end');
  const lo = Math.max(0, Math.min(a, b));
  const hi = Math.min(original.length_m, Math.max(a, b));
  if (hi - lo < 1)
    throw new LnsError(
      `The patch must cover at least 1 m of line ${original.number} (0 to ${Math.round(original.length_m).toLocaleString('en-AU')} m).`,
    );
  return { from_m: Math.round(lo * 100) / 100, to_m: Math.round(hi * 100) / 100 };
}

/** The row of a patch of `original` over `segment`, numbered `number`, in the line's planned direction. */
export function patchRow(
  original: Pick<PlannedLine, 'x1' | 'y1' | 'x2' | 'y2' | 'length_m'> & Partial<PlannedLine>,
  segment: Segment,
  number: number,
): LnsRow {
  const line = original as PlannedLine;
  const [x1, y1] = pointAlong(line, segment.from_m);
  const [x2, y2] = pointAlong(line, Math.min(segment.to_m, original.length_m));
  const r = (v: number): number => Math.round(v * 100) / 100;
  return { number, x1: r(x1), y1: r(y1), x2: r(x2), y2: r(y2) };
}

/** The row of a planned line (a patch's or reflight's own ends as Line review planned them). */
export function plannedRow(l: Pick<PlannedLine, 'number' | 'x1' | 'y1' | 'x2' | 'y2'>): LnsRow {
  return { number: l.number, x1: l.x1, y1: l.y1, x2: l.x2, y2: l.y2 };
}

/**
 * The next free version of a line for a new patch: one above the highest version of the line already planned or
 * flown (`taken`), as Line review numbers reflights. Throws when the version digit is used up (9).
 */
export function nextVersion(lineNumber: number, taken: Iterable<number>): number {
  const base = parseLineNumber(lineNumber).base;
  let top = lineNumber;
  for (const n of taken) if (n >= base && n <= base + 9 && n > top) top = n;
  if (top - base >= 9) throw new LnsError(`Line ${base} has no version digit left for another patch.`);
  return top + 1;
}

// ------------------------------------------------------------------------------------------------- merge and split

/** A file to merge: its name (for messages) and its contents. */
export interface LnsSource {
  readonly name: string;
  readonly doc: LnsDocument;
}

/** Several lines files as one (F16): refused across UTM zones; a line number in two files is kept once, the first. */
export function mergeLns(sources: readonly LnsSource[]): {
  readonly header: string;
  readonly eol: '\r\n' | '\n';
  readonly rows: LnsRow[];
  readonly notes: string[];
} {
  if (sources.length < 2) throw new LnsError('Choose two or more lines files to merge.');
  const first = sources[0]!;
  const zoneOf = (s: LnsSource): string =>
    s.doc.parsed ? `${s.doc.parsed.zone}${s.doc.parsed.hemisphere}` : 'unknown';
  for (const s of sources) {
    if (zoneOf(s) !== zoneOf(first))
      throw new LnsError(
        `${s.name} is in UTM zone ${zoneOf(s)} and ${first.name} in zone ${zoneOf(first)}: merge files of one zone only.`,
      );
  }
  const seen = new Map<number, string>();
  const rows: LnsRow[] = [];
  const notes: string[] = [];
  for (const s of sources) {
    for (const r of s.doc.rows) {
      const already = seen.get(r.number);
      if (already !== undefined) {
        notes.push(`Line ${r.number} is in ${already} and ${s.name}: kept from ${already}.`);
        continue;
      }
      seen.set(r.number, s.name);
      rows.push(r);
    }
  }
  return { header: first.doc.header, eol: first.doc.eol, rows, notes };
}

export type LnsSplit = 'block' | 'kind';

/**
 * One lines file as several (F16): by block (the first digit of the line number) or by kind (traverses and ties).
 * Each part keeps the file's header and line ending; `suffix` names it (`B1`, `Traverses`, `Ties`). Rows below 10000
 * (test, comp-box and ground-check lines) go in a part of their own, `Other`.
 */
export function splitLns(
  doc: LnsDocument,
  by: LnsSplit,
): Array<{ readonly suffix: string; readonly rows: LnsRow[] }> {
  const parts = new Map<string, LnsRow[]>();
  for (const r of doc.rows) {
    const p = parseLineNumber(r.number);
    const key = !p.production
      ? 'Other'
      : by === 'block'
        ? `B${p.blockDigit}`
        : p.kind === 'tie'
          ? 'Ties'
          : 'Traverses';
    const list = parts.get(key) ?? [];
    list.push(r);
    parts.set(key, list);
  }
  if (parts.size < 2)
    throw new LnsError(
      by === 'block'
        ? 'Every line of this file is in one block: there is nothing to split.'
        : 'This file holds only one kind of line: there is nothing to split.',
    );
  const order = ['Traverses', 'Ties', 'Other'];
  return [...parts.entries()]
    .sort(([a], [b]) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return ia >= 0 || ib >= 0 ? (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) : a.localeCompare(b);
    })
    .map(([suffix, rows]) => ({ suffix, rows }));
}

// ------------------------------------------------------------------------------------------------- names

/** The folder a refly lines file and the refly workbook are saved in, inside the block's `1. Flight Plan`. */
export const REFLY_DIR = 'Reflys';

/** `YYYYMMDD` of a local date `YYYY-MM-DD`. */
const compact = (isoDate: string): string => isoDate.replaceAll('-', '');

/**
 * A clear name for a refly lines file of a block: `<block folder>_Refly_<YYYYMMDD>.lns`, with `_2`, `_3`… when a
 * file of that name is already there (`taken`, compared without case). Never ends in `_LNS.lns`, so it is never
 * taken for the block's own lines file.
 */
export function reflyLnsName(blockFolder: string, isoDate: string, taken: Iterable<string>): string {
  return freeLinesFileName(`${blockFolder}_Refly_${compact(isoDate)}`, taken);
}

/**
 * `<base>.lns`, else `<base>_2.lns`…: the first name whose `.lns`, `.xyz` and `.KML` are all free in `taken`
 * (case-insensitive), so the three files of a refly or patch lines file share one base name (decision 831).
 */
export function freeLinesFileName(base: string, taken: Iterable<string>): string {
  const used = new Set([...taken].map((t) => t.toLowerCase()));
  const free = (b: string): boolean =>
    ['.lns', '.xyz', '.kml'].every((ext) => !used.has(`${b}${ext}`.toLowerCase()));
  if (free(base)) return `${base}.lns`;
  for (let i = 2; ; i++) if (free(`${base}_${i}`)) return `${base}_${i}.lns`;
}

/** `<base><ext>`, else `<base>_2<ext>`, `<base>_3<ext>`… (case-insensitive against `taken`). */
export function freeName(base: string, ext: string, taken: Iterable<string>): string {
  const used = new Set([...taken].map((t) => t.toLowerCase()));
  if (!used.has(`${base}${ext}`.toLowerCase())) return `${base}${ext}`;
  for (let i = 2; ; i++) {
    const name = `${base}_${i}${ext}`;
    if (!used.has(name.toLowerCase())) return name;
  }
}

/** A file name the core will take: letters, digits, spaces, `_ - . ( )` only, ending in `.lns`. */
export function checkLnsName(name: string): string | null {
  const n = name.trim();
  if (!/^[\w\- .()]+\.lns$/i.test(n))
    return 'Use letters, digits, spaces, "_", "-" or "." and end the name in .lns.';
  if (/_LNS\.lns$/i.test(n)) return 'Names ending in _LNS.lns are kept for the block lines files.';
  return null;
}

// ------------------------------------------------------------------------------------------------- preview

/** Planned direction of a row in degrees from grid north (0 to 359), from its first point to its second. */
export function rowBearing(r: Pick<LnsRow, 'x1' | 'y1' | 'x2' | 'y2'>): number {
  const deg = (Math.atan2(r.x2 - r.x1, r.y2 - r.y1) * 180) / Math.PI;
  return Math.round((deg + 360) % 360) % 360;
}

/** The bearing in words for the screen and the workbook: `090° (E)`. */
export function bearingWords(deg: number): string {
  const names = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const d = ((Math.round(deg) % 360) + 360) % 360;
  return `${String(d).padStart(3, '0')}° (${names[Math.round(d / 45) % 8]})`;
}

/** Length of a row in metres (planar UTM). */
export function rowLength(r: Pick<LnsRow, 'x1' | 'y1' | 'x2' | 'y2'>): number {
  return Math.round(Math.hypot(r.x2 - r.x1, r.y2 - r.y1) * 100) / 100;
}

/** The extent of some rows for a preview: [minX, minY, maxX, maxY], grown by `pad` of the larger side. */
export function rowsExtent(rows: readonly LnsRow[], pad = 0.05): [number, number, number, number] | null {
  if (rows.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of rows) {
    minX = Math.min(minX, r.x1, r.x2);
    maxX = Math.max(maxX, r.x1, r.x2);
    minY = Math.min(minY, r.y1, r.y2);
    maxY = Math.max(maxY, r.y1, r.y2);
  }
  const p = Math.max(maxX - minX, maxY - minY, 1) * pad;
  return [minX - p, minY - p, maxX + p, maxY + p];
}
